import React, { useState } from 'react';
import { Send, User, Phone, MessageSquare, CheckCircle, Mail } from 'lucide-react';
import Button from '../common/Button';
import type { Job } from '../../types/jobs';

interface QuickApplicationFormProps {
  job: Job;
  onClose: () => void;
}

export default function QuickApplicationForm({ job, onClose }: QuickApplicationFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Client-side validation
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Valid email address is required');
      return;
    }
    if (!formData.phone.trim()) {
      setError('Phone number is required');
      return;
    }
    if (!formData.message.trim()) {
      setError('Message is required');
      return;
    }
    if (formData.message.length > 2000) {
      setError('Message must be less than 2000 characters');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('Job ID:', job.id);
      console.log('Form data:', formData);
      
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-job-application`;
      
      const requestBody = {
        jobId: job.id,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        message: formData.message
      };
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        // Handle partial success (application saved but emails failed)
        if (result.applicationId) {
          console.warn('Application saved but emails failed:', result.emailStatus);
          // Still show success to user since application was saved
          setSuccess(true);
        } else {
          throw new Error(result.message || 'Failed to submit application');
        }
      } else {
        setSuccess(true);
      }
      
      // Auto-close after showing success message
      setTimeout(() => {
        onClose();
      }, 4000);

    } catch (err) {
      console.error('Error submitting application:', err);
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-md w-full p-6 text-center">
          <div className="bg-green-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          
          <h2 className="text-2xl font-semibold mb-2 text-gray-900">Application Sent Successfully!</h2>
          <p className="text-gray-600 mb-4">
            Your application for <strong>{job.title}</strong> has been sent successfully!
          </p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <p className="text-green-800 text-sm">
              📧 <strong>Notification emails sent:</strong>
            </p>
            <p className="text-green-700 text-sm">
              • Employer notification sent
            </p>
            {formData.email && (
              <p className="text-green-700 text-sm">
                • {formData.email} (your confirmation)
              </p>
            )}
          </div>
          <p className="text-sm text-gray-500">
            The employer will contact you at <strong>{formData.phone}</strong> if they're interested.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-md mx-auto p-4 sm:p-6 relative my-2 sm:my-4 max-h-[98vh] sm:max-h-[95vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          ×
        </button>

        <div className="mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-2xl font-semibold mb-1 sm:mb-2">Apply for Position</h2>
          <p className="text-sm sm:text-base text-gray-600">
            Applying for <strong>{job.title}</strong>
          </p>
          <div className="mt-4 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-blue-900 mb-1">Save Time on Future Applications</p>
                <p className="text-xs sm:text-sm text-blue-800 leading-relaxed">
                  Sign up to save your resume and cover letter. Next time you apply, everything will be auto-filled. No more typing the same information over and over!
                </p>
                <a
                  href="/signup"
                  className="inline-block mt-2 text-xs sm:text-sm font-semibold text-blue-700 hover:text-blue-900 underline"
                >
                  Create free account →
                </a>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-red-800 text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              <User className="h-4 w-4 inline mr-1" />
              Full Name *
            </label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full rounded-md border border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 px-3 py-2 text-sm sm:text-base"
              placeholder="Enter your full name"
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              <Mail className="h-4 w-4 inline mr-1" />
              Email Address *
            </label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full rounded-md border border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 px-3 py-2 text-sm sm:text-base"
              placeholder="your.email@example.com"
              required
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              <Phone className="h-4 w-4 inline mr-1" />
              Phone Number *
            </label>
            <input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full rounded-md border border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 px-3 py-2 text-sm sm:text-base"
              placeholder="(555) 123-4567"
              required
            />
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
              <MessageSquare className="h-4 w-4 inline mr-1" />
              Cover Letter / Message *
            </label>
            <textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              className="w-full rounded-md border border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 px-3 py-2 text-sm sm:text-base"
              rows={4}
              placeholder="Tell the employer why you're interested in this position and highlight your relevant experience..."
              required
            />
          </div>

          <div className="pt-2">
            <Button
              type="submit"
              variant="primary"
              icon={Send}
              isLoading={loading}
              fullWidth
              size="lg"
              className="text-base py-3"
            >
              {loading ? 'Sending Application...' : 'Submit Application'}
            </Button>
          </div>

          <div className="space-y-2">
            <p className="text-xs text-gray-500 text-center">
              Your application will be sent directly to the employer via email.
              You'll also receive a confirmation copy at your email address.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
              <p className="text-xs font-medium text-amber-900">
                Want to skip this form next time?
              </p>
              <p className="text-xs text-amber-800 mt-1">
                Sign up to save your info and apply to jobs in one click!
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}